/* =====================================================================
   Entity micro-simulation — the success engine.
   ---------------------------------------------------------------------
   A small, deterministic stock-flow model of one entity (Phase 1:
   companies), stepped yearly 2026..2076, driven by the World3-derived
   drivers of the chosen scenario:

     stocks   K  productive capacity        (2026 = 1)
              B  liquidity buffer           (fraction of annual revenue)
              A  adaptability               (0..1)

     flows    revenue = min(K, exposed demand)     -- overcapacity idles
              costs   = inputs*scarcity + pollution burden + fixed on K
                        + intervention capex/opex
              shocks  = hazard * exposure, damped by resilience &
                        adaptability, hitting K and B
              growth  = investment out of the buffer, gated by buffer
                        health and by demand headroom

   Interventions act through four time-ramped levers (noisy-OR blend),
   their economics (size -> capex, opex, speed -> ramp tau) and tags
   (regen -> transition capture, services -> sector-mix shift).

   Death is an absorbing state: insolvency, capacity collapse, or three
   consecutive distress years.

   Success index 0..100 blends thriving (revenue vs today) and viability
   (buffer & margin health); tiles show thriving vs today in %.

   Explainability is a first-class output (ARCHITECTURE §6.4): driver
   contributions via frozen-counterfactual decomposition, leave-one-out
   intervention marginals, and event markers.

   Every constant lives in CALIB. Deterministic: no randomness anywhere.
   ===================================================================== */
import {
  type Entity, type Strategy, type InterventionProfile, type Levers, type LeverKey,
  LEVER_KEYS, IV_SIZE, IV_OPEX, IV_SPEED, PRIORITY_INTENSITY, sectorWeights,
  type SectorWeights, type CellTrace, type DriverContributions, type TraceEvent,
  type InterventionMarginal, type HorizonScore,
} from '@flourish/schema';
import { worldDrivers, demandCore, demandPeakYear, SIM_YEARS, SIM_START, HORIZONS, type WorldDrivers } from './drivers.js';
import type { ScenarioId } from './scenarios.js';

export const ENGINE_VERSION = '0.1.0-m1';

/* ---------- calibration constants (single source of truth) ---------- */
export const CALIB = {
  // unit economics (fractions of revenue unless noted)
  margin0: 0.12,               // baseline net margin every entity starts at
  fixedOnCapacity: 0.20,       // fixed cost per unit of CAPACITY (overcapacity drag)
  inputShare: { Low: 0.15, Medium: 0.25, High: 0.38 } as Record<string, number>,
  pollutionShare: { Low: 0.05, Medium: 0.10, High: 0.18 } as Record<string, number>,
  scarcityCap: 2.2,            // substitution/pass-through ceiling on input-cost index
  pollutionCap: 2.5,           // saturation of pollution/regulatory burden
  unitCostFloorMargin: -0.05,  // firms curtail production losing more than this per unit
  effInputDamp: 0.5,           // efficiency lever damps input costs
  effPollDamp: 0.5,            // efficiency lever damps pollution burden
  resInputDamp: 0.45,          // resilience (hedging) damps input-cost spikes
  adaptCostDamp: 0.20,         // adaptability damps both cost channels
  adaptMarginLift: 0.08,       // margin gained per point of adaptability GROWN beyond start
  passThroughInput: 0.75,      // share of industry-wide input-cost INCREASES passed to prices
  passThroughPoll: 0.6,        // share of pollution/regulatory cost increases passed on

  // demand & transition
  divExposure: 0.6,            // max share of demand re-based to the broad mix
  transitionDemand: 0.25,      // transition demand captured via efficiency+diversification
  regenDemand: 0.35,           // extra transition demand for regen-tagged portfolios
  servicesShiftMax: 0.5,       // max sector-mix morph from services-tagged interventions

  // shocks
  shockResDamp: 0.7,           // resilience lever damps shock damage
  shockAdaptDamp: 0.5,         // adaptability damps shock damage
  shockDivExposure: 0.3,       // diversification reduces shock exposure
  shockToCapacity: 0.5,        // share of shock damage destroying K
  shockToBuffer: 0.35,         // share of shock damage as one-off cost to B

  // growth & capital
  investMax: 0.12,             // max investment rate (fraction of K per year)
  capexPerCapacity: 1.5,       // buffer units to build one unit of K
  overcapacityGate: 1.15,      // no expansion when K exceeds demand by this factor
  overcapacityInvestScale: 0.15,
  depreciation: 0.04,          // capacity decay when maintenance is unaffordable
  // consolidation: voluntary downsizing toward demand under overcapacity
  shedBase: 0.05,              // base annual shed rate of excess capacity
  shedAdaptive: 0.10,          // extra shed rate from adaptability + resilience (proactive firms)
  shedRecovery: 0.3,           // cash recovered per unit of capacity shed

  // buffer & distress
  buffer0: { company: 0.33, government: 1.0, ngo: 0.5, individual: 0.75 } as Record<string, number>,
  bufferMax: 1.2,
  investGateLo: 0.08,          // buffer below which no growth investment
  investGateHi: 0.25,          // buffer above which investment is unconstrained
  fireSaleRate: 0.7,           // cents on the unit for distressed capacity sales
  fireSaleMaxShare: 0.4,       // max share of K sellable in one year
  insolvencyFloor: -0.05,      // buffer below this after fire sale = death
  distressYearsToDeath: 4,
  capacityFloor: 0.12,         // K below this = the organisation is gone

  // entity type base parameters (prototype ENTITY_TYPES)
  typeGrowth: { company: 0.82, government: 0.4, ngo: 0.34, individual: 0.5 } as Record<string, number>,
  typeResilience: { company: 0.34, government: 0.62, ngo: 0.58, individual: 0.5 } as Record<string, number>,
  adaptive0: { Low: 0.3, Medium: 0.5, High: 0.7 } as Record<string, number>,
  adaptLearn: 0.04,

  // scoring
  scoreThriveWeight: 0.5,
  scoreViabilityWeight: 0.5,
  thriveScale: 1.8,            // thriving of 1.8x today saturates the thrive component
  // buffer level that reads as "fully healthy", per type — scaled to each
  // type's NORMAL reserves so every entity starts at the same anchor and
  // differences emerge from dynamics, not from institutional starting cash
  viabilityBufferRef: { company: 0.6, government: 1.8, ngo: 0.9, individual: 1.35 } as Record<string, number>,
  viabilityMarginRef: 0.2,     // margin span mapped onto health (from -0.05 to +0.15)
  pctClamp: [-100, 400] as [number, number],
} as const;

/* ---------- prepared interventions ---------- */
interface PreparedIv {
  iid: string;
  name: string;
  m: number;                   // priority intensity
  tau: number;                 // ramp time constant, years
  capexTotal: number;          // total one-off spend (fraction of annual revenue)
  opexD: number;               // ongoing cost delta at full ramp
  levers: Levers;
  regen: boolean;
  services: boolean;
}

function prepare(strategy: Strategy): PreparedIv[] {
  return (strategy.interventions ?? []).map(iv => ({
    iid: iv.iid,
    name: iv.name,
    m: PRIORITY_INTENSITY[iv.priority],
    tau: IV_SPEED[iv.speed].tau,
    capexTotal: IV_SIZE[iv.size].capex * PRIORITY_INTENSITY[iv.priority],
    opexD: IV_OPEX[iv.opex].d * PRIORITY_INTENSITY[iv.priority],
    levers: iv.levers,
    regen: iv.tags.includes('regen'),
    services: iv.tags.includes('services'),
  }));
}

const ramp = (t: number, tau: number) => 1 - Math.exp(-Math.max(0, t) / tau);

/** Noisy-OR blend of lever k across ramped interventions at step t. */
function leverAt(ivs: PreparedIv[], k: LeverKey, t: number): number {
  let neg = 1;
  for (const iv of ivs) neg *= 1 - (iv.levers[k] ?? 0) * iv.m * ramp(t, iv.tau);
  return 1 - neg;
}

function taggedShare(ivs: PreparedIv[], tag: 'regen' | 'services', t: number, weight: number): number {
  let neg = 1;
  for (const iv of ivs) if (iv[tag]) neg *= 1 - weight * iv.m * ramp(t, iv.tau);
  return 1 - neg;
}

/* ---------- simulation ---------- */
export interface Freeze {
  demand?: boolean;
  scarcity?: boolean;
  pollution?: boolean;
  hazard?: boolean;
  transition?: boolean;
  zeroLevers?: boolean;
}

export interface SimResult {
  years: number[];
  score: number[];             // composite 0..100 (0 from death onward)
  thriving: number[];          // revenue vs today (1 = today)
  viability: number[];         // 0..1
  capacity: number[];
  buffer: number[];
  deathYear: number | null;
  events: TraceEvent[];
}

const clamp01 = (v: number) => Math.max(0, Math.min(1, v));

export function simulate(entity: Entity, strategy: Strategy, scenarioId: ScenarioId, freeze: Freeze = {}): SimResult {
  const C = CALIB;
  const d = worldDrivers(scenarioId);
  const ivs = freeze.zeroLevers ? [] : prepare(strategy);
  const p = entity.profile;

  const cIn = C.inputShare[p.resourceDep] ?? C.inputShare['Medium'];
  const cPol = C.pollutionShare[p.footprint] ?? C.pollutionShare['Medium'];
  const A0forCal = C.adaptive0[p.adaptive] ?? 0.5;
  const adaptDamp0 = 1 - C.adaptCostDamp * A0forCal;
  // calibrated so that margin(0) = margin0 exactly, for every profile:
  // entities differ in DYNAMICS, not in their starting position
  const cVar = 1 - cIn * adaptDamp0 - cPol * adaptDamp0 - C.fixedOnCapacity - C.margin0;
  const g0 = C.typeGrowth[entity.type] ?? C.typeGrowth['company'];
  const wtsBase = sectorWeights(p.sector);

  let K = 1, B = C.buffer0[entity.type] ?? 0.33;
  const A0 = C.adaptive0[p.adaptive] ?? 0.5;
  let A = A0;
  let distress = 0;
  let dead = false, deathYear: number | null = null;

  const out: SimResult = {
    years: [], score: [], thriving: [], viability: [], capacity: [], buffer: [],
    deathYear: null, events: [],
  };
  const seen = { bufferFloor: false, overcapacity: false, distress: false };

  const peakYr = demandPeakYear(d, wtsBase);
  if (peakYr > SIM_START && peakYr < SIM_START + SIM_YEARS) {
    out.events.push({ year: peakYr, kind: 'worldPeak', detail: 'world demand for this sector mix peaks' });
  }

  for (let t = 0; t <= SIM_YEARS; t++) {
    const year = SIM_START + t;

    if (dead) {
      out.years.push(year); out.score.push(0); out.thriving.push(0); out.viability.push(0);
      out.capacity.push(0); out.buffer.push(0);
      continue;
    }

    // --- levers & intervention flows at t
    const Leff = leverAt(ivs, 'efficiency', t);
    const Lres = leverAt(ivs, 'resilience', t);
    const Lgro = leverAt(ivs, 'growth', t);
    const Ldiv = leverAt(ivs, 'diversification', t);
    const regenShare = taggedShare(ivs, 'regen', t, C.regenDemand);
    const servShare = taggedShare(ivs, 'services', t, C.servicesShiftMax);

    let capexFlow = 0, opexFlow = 0;
    for (const iv of ivs) {
      const r1 = ramp(t, iv.tau), r0 = t === 0 ? 0 : ramp(t - 1, iv.tau);
      capexFlow += iv.capexTotal * (r1 - r0);
      opexFlow += iv.opexD * r1;
    }

    // --- world inputs (frozen for trace decomposition where requested)
    const wts: SectorWeights = {
      ind: wtsBase.ind * (1 - servShare),
      serv: wtsBase.serv + (wtsBase.ind + wtsBase.food) * servShare * 0.8,
      food: wtsBase.food * (1 - servShare),
      pop: wtsBase.pop,
    };
    const dCore = freeze.demand ? 1 : demandCore(d, wts, t);
    const dBroad = freeze.demand ? 1 : d.demandBroad[t];
    const S = freeze.scarcity ? 1 : Math.min(d.scarcity[t], C.scarcityCap);
    const P = freeze.pollution ? 1 : Math.min(d.pollution[t], C.pollutionCap);
    const H = freeze.hazard ? 0 : d.hazard[t];
    const T = freeze.transition ? 0 : d.transition[t];

    // --- demand exposure & revenue
    const div = C.divExposure * Ldiv;
    const transitionBoost = T * (C.transitionDemand * (0.6 * Leff + 0.4 * Ldiv) + regenShare);
    const dExposed = ((1 - div) * dCore + div * dBroad) * (1 + transitionBoost);
    const volume = Math.min(K, dExposed);
    const revenue = volume;

    // --- unit costs (damped by efficiency, hedging-resilience, adaptability;
    //     industry-wide cost INCREASES partially pass through to prices,
    //     while efficiency gains below baseline are kept in full)
    const adaptDamp = 1 - C.adaptCostDamp * A;
    const inputRaw = cIn * S * (1 - C.effInputDamp * Leff) * (1 - C.resInputDamp * Lres) * adaptDamp;
    const inputCost = inputRaw > cIn ? cIn + (inputRaw - cIn) * (1 - C.passThroughInput) : inputRaw;
    const pollRaw = cPol * P * (1 - C.effPollDamp * Leff) * adaptDamp;
    const pollCost = pollRaw > cPol ? cPol + (pollRaw - cPol) * (1 - C.passThroughPoll) : pollRaw;
    // adaptive firms migrate to more defensible niches as A grows beyond its start
    const adaptLift = C.adaptMarginLift * (A - A0);
    const unitCost = Math.min(inputCost + pollCost + cVar - adaptLift, 1 - C.unitCostFloorMargin);   // curtailment floor
    const operating = revenue * (1 - unitCost) - C.fixedOnCapacity * K - opexFlow * revenue - capexFlow;

    // --- shocks
    const exposure = 1 - C.shockDivExposure * Ldiv;
    const dmg = H * exposure * (1 - C.shockResDamp * Lres) * (1 - C.shockAdaptDamp * A);
    const dmgK = C.shockToCapacity * dmg * K;
    const dmgB = C.shockToBuffer * dmg;

    // --- growth investment (buffer- and demand-gated)
    const gate = clamp01((B - C.investGateLo) / (C.investGateHi - C.investGateLo));
    let invest = C.investMax * g0 * Lgro * gate * K;
    if (K > C.overcapacityGate * dExposed) invest *= C.overcapacityInvestScale;

    // --- consolidation: shed excess capacity toward demand parity
    // (proactive firms faster; the 1.15 overcapacity gate above only stops
    // NEW investment — shedding aims at K ~ demand)
    let shed = 0;
    if (K > dExposed * 1.02) {
      const shedRate = C.shedBase + C.shedAdaptive * (0.5 * A + 0.25 * Lres);
      shed = Math.min(K - dExposed, K * shedRate);
    }

    // --- book flows
    // maintenance keeps K constant while affordable (its cost sits inside
    // fixedOnCapacity); when the buffer is exhausted, maintenance stops
    // and capacity decays
    const decay = B >= C.investGateLo ? 0 : C.depreciation * K;
    B = Math.min(B + operating - invest - dmgB + shed * C.shedRecovery, C.bufferMax);
    K = Math.max(K - decay - shed - dmgK + invest / C.capexPerCapacity, 0);
    A = A + C.adaptLearn * (0.5 * Lres + 0.5 * Ldiv) * (1 - A);

    // --- distress & death
    if (B < 0) {
      const sell = Math.min(K * C.fireSaleMaxShare, -B / C.fireSaleRate);
      K -= sell;
      B += sell * C.fireSaleRate;
      distress += 1;
      if (!seen.distress) { seen.distress = true; out.events.push({ year, kind: 'distress', detail: 'buffer exhausted — forced capacity sale' }); }
      if (B < C.insolvencyFloor || distress >= C.distressYearsToDeath || K < C.capacityFloor) {
        dead = true; deathYear = year;
        out.events.push({ year, kind: 'death', detail: distress >= C.distressYearsToDeath ? 'sustained distress — insolvency' : 'assets exhausted' });
      }
      if (B < 0) B = 0;
    } else {
      distress = 0;
      if (K < C.capacityFloor) {
        dead = true; deathYear = year;
        out.events.push({ year, kind: 'death', detail: 'capacity below viability threshold' });
      }
    }

    if (!seen.bufferFloor && !dead && B < 2 / 12 && t > 0) {
      seen.bufferFloor = true;
      out.events.push({ year, kind: 'bufferFloor', detail: 'liquidity buffer below 2 months' });
    }
    if (!seen.overcapacity && K > 1.3 * dExposed && t > 2) {
      seen.overcapacity = true;
      out.events.push({ year, kind: 'overcapacity', detail: 'capacity exceeds exposed demand by >30%' });
    }

    // --- indices
    const thriving = dead ? 0 : revenue;                        // revenue(0) = 1 by construction
    const marginRate = revenue > 0 ? operating / revenue : -1;
    const viability = dead ? 0 : clamp01(
      0.6 * clamp01(B / (C.viabilityBufferRef[entity.type] ?? 0.6)) +
      0.4 * clamp01((marginRate + 0.05) / C.viabilityMarginRef));
    const score = dead ? 0 : 100 * clamp01(
      C.scoreThriveWeight * Math.min(thriving, C.thriveScale) / C.thriveScale +
      C.scoreViabilityWeight * viability);

    out.years.push(year);
    out.score.push(score);
    out.thriving.push(thriving);
    out.viability.push(viability);
    out.capacity.push(K);
    out.buffer.push(B);
  }

  out.deathYear = deathYear;
  return out;
}

/* ---------- cell scoring (what the matrix consumes) ---------- */
export interface CellResult {
  horizons: HorizonScore[];
  endScore: number;
  deathYear: number | null;
  sim: SimResult;
}

export function scoreCell(entity: Entity, strategy: Strategy, scenarioId: ScenarioId): CellResult {
  const sim = simulate(entity, strategy, scenarioId);
  const base = sim.thriving[0] || 1;
  const horizons: HorizonScore[] = HORIZONS.map(h => {
    const dead = sim.deathYear !== null && sim.deathYear <= SIM_START + h;
    const pct = dead ? -100 :
      Math.max(CALIB.pctClamp[0], Math.min(CALIB.pctClamp[1], 100 * (sim.thriving[h] / base - 1)));
    return { years: h, pct: Math.round(pct), score: Math.round(sim.score[h]), death: dead };
  });
  return { horizons, endScore: Math.round(sim.score[SIM_YEARS]), deathYear: sim.deathYear, sim };
}

/* ---------- explainability trace (ARCHITECTURE §6.4) ---------- */
function fnv1a(str: string): string {
  let h = 0x811c9dc5;
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return (h >>> 0).toString(16).padStart(8, '0');
}

export function inputsHash(entity: Entity, strategy: Strategy, scenarioId: string): string {
  return fnv1a(JSON.stringify({
    e: { type: entity.type, p: entity.profile },
    s: strategy.interventions.map(iv => ({
      l: iv.levers, z: iv.size, o: iv.opex, v: iv.speed, t: iv.tags, pr: iv.priority,
    })),
    scn: scenarioId,
  }));
}

/** Utility of a run: mean score over the standard horizons (5/15/30/50y).
    The same objective the No-Regret optimizer maximizes — decomposition and
    optimization speak the same language, and pre-death dynamics still count
    in worlds where the entity eventually dies. */
function horizonMean(score: number[]): number {
  let s = 0;
  for (const h of HORIZONS) s += score[h];
  return s / HORIZONS.length;
}

export function traceCell(entity: Entity, strategy: Strategy, scenarioId: ScenarioId): CellTrace {
  const actual = scoreCell(entity, strategy, scenarioId);
  const deltaTotal = horizonMean(actual.sim.score) - actual.sim.score[0];

  // frozen-counterfactual contributions: what part of the change vs today
  // disappears when a driver is held at its 2026 baseline?
  const contrib = (freeze: Freeze): number => {
    const f = simulate(entity, strategy, scenarioId, freeze);
    return deltaTotal - (horizonMean(f.score) - f.score[0]);
  };
  const drivers: DriverContributions = {
    demand: contrib({ demand: true }),
    inputScarcity: contrib({ scarcity: true }),
    pollutionBurden: contrib({ pollution: true }),
    hazardShocks: contrib({ hazard: true }),
    transitionOpportunity: contrib({ transition: true }),
    strategyEffects: contrib({ zeroLevers: true }),
    interaction: 0,
  };
  drivers.interaction = deltaTotal -
    (drivers.demand + drivers.inputScarcity + drivers.pollutionBurden +
     drivers.hazardShocks + drivers.transitionOpportunity + drivers.strategyEffects);

  // leave-one-out intervention marginals (horizon-mean basis)
  const d = worldDrivers(scenarioId);
  const H = SIM_YEARS;
  const interventions: InterventionMarginal[] = (strategy.interventions ?? []).map(iv => {
    const without: Strategy = { ...strategy, interventions: strategy.interventions.filter(x => x.iid !== iv.iid) };
    const w = simulate(entity, without, scenarioId);
    const marginal = horizonMean(actual.sim.score) - horizonMean(w.score);
    const rampedShare = ramp(H, IV_SPEED[iv.speed].tau);
    let note: string | undefined;
    if ((iv.levers.growth ?? 0) >= 0.5 && d.demandBroad[H] < 0.8) note = 'overcapacity vs falling demand';
    if ((iv.levers.resilience ?? 0) >= 0.6 && actual.deathYear === null && w.deathYear !== null) note = 'prevents insolvency in this world';
    if (marginal < -1 && (iv.levers.resilience ?? 0) >= 0.6 && w.deathYear === null && actual.deathYear === null)
      note = 'redundant here — portfolio already carries this protection';
    return { iid: iv.iid, name: iv.name, marginal: Math.round(marginal * 10) / 10, rampedShare: Math.round(rampedShare * 100) / 100, note };
  });
  interventions.sort((a, b) => Math.abs(b.marginal) - Math.abs(a.marginal));

  return {
    cell: { entityId: entity.id, strategyId: strategy.id, scenarioId },
    horizons: actual.horizons,
    endScore: actual.endScore,
    deathYear: actual.deathYear,
    drivers: Object.fromEntries(Object.entries(drivers).map(([k, v]) => [k, Math.round(v * 10) / 10])) as unknown as DriverContributions,
    interventions,
    events: actual.sim.events,
    engineVersion: ENGINE_VERSION,
    inputsHash: inputsHash(entity, strategy, scenarioId),
  };
}
